import React, { useEffect, useState } from 'react';
import { adminGalleryService } from '../services';
import { FaTrash, FaPlus } from 'react-icons/fa';
import { resolveMediaUrl } from '../services/api';
import { notifyError, notifySuccess } from '../utils/toast';

const Gallery = () => {
    const [images, setImages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newCategory, setNewCategory] = useState('');
    const [uploadForm, setUploadForm] = useState({
        image: null,
        alt_text: '',
        gallery_category_id: '',
    });

    useEffect(() => {
        fetchGallery();
        fetchCategories();
    }, []);

    const fetchGallery = async () => {
        try {
            const response = await adminGalleryService.getAll();
            setImages(response.data.data || []);
        } catch (error) {
            notifyError('Unable to load gallery.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await adminGalleryService.getCategories();
            setCategories(response.data || []);
        } catch (error) {
            notifyError('Unable to load categories.');
        }
    };

    const handleFileChange = (e) => {
        setUploadForm((prev) => ({ ...prev, image: e.target.files[0] }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUploadForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        if (!newCategory.trim()) return;
        try {
            await adminGalleryService.createCategory(newCategory.trim());
            notifySuccess('Category added successfully.');
            setNewCategory('');
            await fetchCategories();
        } catch (error) {
            notifyError(error.response?.data?.message || 'Unable to add category.');
        }
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm('Delete this category?')) return;
        try {
            await adminGalleryService.deleteCategory(id);
            notifySuccess('Category deleted successfully.');
            await fetchCategories();
        } catch (error) {
            notifyError('Unable to delete category.');
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!uploadForm.image || !uploadForm.gallery_category_id) {
            notifyError('Image and category are required.');
            return;
        }

        const formData = new FormData();
        formData.append('image', uploadForm.image);
        formData.append('alt_text', uploadForm.alt_text);
        formData.append('gallery_category_id', uploadForm.gallery_category_id);

        try {
            await adminGalleryService.upload(formData);
            notifySuccess('Image uploaded successfully.');
            await fetchGallery();
            setUploadForm({ image: null, alt_text: '', gallery_category_id: '' });
        } catch (error) {
            notifyError('Unable to upload image.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            await adminGalleryService.delete(id);
            notifySuccess('Image deleted successfully.');
            await fetchGallery();
        } catch (error) {
            notifyError('Unable to delete image.');
        }
    };

    return (
        <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-8">Gallery</h1>

            <div className="bg-white p-8 rounded-lg shadow-lg mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Manage Categories</h2>
                <form onSubmit={handleCreateCategory} className="flex gap-3 mb-4">
                    <input
                        type="text"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        placeholder="Add new category"
                        className="flex-1 rounded-lg border border-gray-300 px-4 py-2"
                    />
                    <button type="submit" className="rounded-lg bg-emerald-600 px-5 py-2 text-white hover:bg-emerald-700">Add</button>
                </form>
                <div className="flex flex-wrap gap-3">
                    {categories.map((category) => (
                        <div key={category.id} className="flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
                            <span>{category.name}</span>
                            <button onClick={() => handleDeleteCategory(category.id)} className="text-red-600 hover:text-red-700">
                                <FaTrash size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Upload Image</h2>
                <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block font-semibold text-gray-700 mb-2">Image</label>
                        <input type="file" onChange={handleFileChange} accept="image/*" required className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div>
                        <label className="block font-semibold text-gray-700 mb-2">Alt Text</label>
                        <input type="text" name="alt_text" value={uploadForm.alt_text} onChange={handleInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block font-semibold text-gray-700 mb-2">Category</label>
                        <select name="gallery_category_id" value={uploadForm.gallery_category_id} onChange={handleInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                            <option value="">Select category</option>
                            {categories.map((category) => (
                                <option key={category.id} value={category.id}>{category.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <button type="submit" className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition flex items-center gap-2">
                            <FaPlus /> Upload Image
                        </button>
                    </div>
                </form>
            </div>

            {loading ? (
                <div className="text-center py-12"><div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {images.map((image) => (
                        <div key={image.id} className="relative group bg-white rounded-lg shadow-lg overflow-hidden">
                            <img src={resolveMediaUrl(image.image_path)} alt={image.alt_text} className="w-full h-48 object-cover group-hover:opacity-75 transition" />
                            <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-40 transition flex items-center justify-center">
                                <button onClick={() => handleDelete(image.id)} className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition"><FaTrash /></button>
                            </div>
                            <div className="p-3">
                                <p className="text-sm font-semibold text-gray-800 truncate">{image.alt_text}</p>
                                <p className="text-xs text-gray-600">{image.gallery_category?.name || image.category}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Gallery;
